import React from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  BarElement,
  BarController,
  TimeScale,
  Tooltip,
  ChartOptions,
  ChartData
} from 'chart.js'
import { Chart, } from 'react-chartjs-2'
import annotationPlugin from 'chartjs-plugin-annotation'
import { useGlobalState } from 'state'
import 'chartjs-adapter-moment'
import { ObservatoryValue, ObservatoryRow } from 'common/types'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  BarElement,
  BarController,
  TimeScale,
  Tooltip,
  annotationPlugin
)

type RainChartProps = {
  rain: ObservatoryRow
  timeInterval: number
  height: number
  timeSeries: ObservatoryValue[]
}

export const RainChart = ({timeInterval, height, timeSeries}: RainChartProps) => {
  const state = useGlobalState()
  const [chartData, setChartData] = React.useState<ChartData>({labels: [], datasets: []})
  const [chartOptions, setChartOptions] = React.useState<ChartOptions>({})
  const chartRef = React.useRef<ChartJS>(null)

  React.useEffect(() => {
    const handleVisibilityChange = () => {
      chartRef.current?.update()
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  React.useEffect(() => {
    loadTimeSeries()
  }, [timeSeries])

  const getMaxTotal = (): number => {
    let total = Math.max(...(timeSeries.map(ts => ts.total).filter(ts => ts !== null) as number[]))
    total = Math.ceil(total + 10)
    if (!total || total < 100) {
      total = 100
    }
    return total
  }

  const getMaxMinutesVal = (): number => {
    const fieldName = timeInterval === 60 ? 'min60' : 'min10'
    let minval = Math.max(...(timeSeries.map(ts => ts[fieldName]).filter(ts => ts !== undefined && ts !== null) as number[]))
    minval = Math.ceil(minval + 1)
    if (!minval || minval < 5) {
      minval = 5
    }
    return minval
  }

  const loadTimeSeries = () => {
    console.log('loadTimeSeries')
    const labels: string[] = []
    const totals: (number | null)[] = []
    const minValues: (number | null)[] = []

    timeSeries.forEach(ts => {
      labels.push(ts.date)
      if (timeInterval === 60) {
        minValues.push(ts.min60 === undefined ? null : Number(ts.min60.toFixed(2)))
      } else {
        minValues.push(ts.min10 === undefined ? null : Number(ts.min10.toFixed(2)))
      }
      totals.push(ts.total === undefined ? null : Number(ts.total.toFixed(2)))
    })

    setChartOptions({
      responsive: false,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false
      },
      scales: {
        xDate: {
          type: 'time',
          time: {
            tooltipFormat: 'MM/DD HH:mm',
            parser: 'YYYY/MM/DD HH:mm',
            unit: 'hour',
            minUnit: 'minute',
            displayFormats: {
              'hour': 'HH:mm',
            }
          },
          grid: {
            display: false
          },
          min: timeSeries[0].date,
          max: timeSeries.slice(-1)[0].date
        },
        yTotal: {
          position: 'right',
          ticks: {
            stepSize: 20
          },
          max: getMaxTotal(),
          beginAtZero: true
        },
        yMinVal: {
          position: 'left',
          ticks: {
            stepSize: 1
          },
          grid: {
            display: false
          },
          max: getMaxMinutesVal(),
          min: 0,
          beginAtZero: true
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              let label = context.dataset.label + ': ' || ''
              if (context.parsed.y !== null) {
                label += context.parsed.y + ' mm'
              }
              return label
            }
          }
        },
        annotation: {
          annotations: {
            nowLine: {
              borderColor: '#FF0000',
              borderWidth: 1.5,
              type: 'line',
              scaleID: 'xDate',
              value: state.app.baseDate.local().format('YYYY/MM/DD HH:mm'),
              label: {
                content: '現在',
                position: 'start',
                display: true
              }
            }
          }
        }
      }
    })

    setChartData({
      labels: labels,
      datasets: [
        {
          type: 'line' as const,
          label: '累加雨量',
          data: totals,
          order: 1,
          pointBackgroundColor: 'white',
          fill: false,
          xAxisID: 'xDate',
          yAxisID: 'yTotal',
          borderWidth: 2,
          borderColor: '#91C46C',
          backgroundColor: '#91C46C',
          pointBorderColor: '#91C46C',
          pointRadius: 2,
          pointHitRadius: 0,
          spanGaps: true
        },
        {
          type: 'bar' as const,
          label: `${timeInterval}分雨量`,
          data: minValues,
          order: 2,
          xAxisID: 'xDate',
          yAxisID: 'yMinVal',
          backgroundColor: '#1C344C',
          borderColor: '#1C344C',
          borderWidth: 0.5
        },
      ]
    })

  }

  return <Chart ref={chartRef} type='bar' data={chartData} height={height} options={chartOptions} />
}
